import React, { useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Grid,
  Alert,
  TableSortLabel,
} from "@mui/material";
import { visuallyHidden } from "@mui/utils";
import { formatToIST } from "../../../utils/Common";

const getRowColor = (days) => {
  if (days > 50) return "#ffcccc";
  if (days === 50) return "#fff8dc";
  return "#ffffff";
};

const getPNLColor = (pnl) => (pnl >= 0 ? "green" : "red");

const PortfolioOverdueTable = ({ stocks, lastCreatedStocksDue, overdueAlert }) => {
  const enrichedStocks = stocks.map((stock) => ({
    ...stock,
    pnl: stock.current_value - stock.book_value,
  }));

  const [orderBy, setOrderBy] = useState("symbol");
  const [order, setOrder] = useState("asc");

  const handleSort = (property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const sortedStocks = enrichedStocks.sort((a, b) => {
    const valA = orderBy === "pnl" ? a.current_value - a.book_value : a[orderBy];
    const valB = orderBy === "pnl" ? b.current_value - b.book_value : b[orderBy];
    return order === "asc" ? valA - valB : valB - valA;
  });

  const totalPNL = enrichedStocks.reduce((sum, stock) => sum + stock.pnl, 0);
  const totalQuantity = enrichedStocks.reduce((sum, stock) => sum + stock.quantity, 0);
  const netStockValue = enrichedStocks.reduce((sum, stock) => sum + stock.current_value, 0);
  const netBookValue = enrichedStocks.reduce((sum, stock) => sum + stock.book_value, 0);
  const inProfit = enrichedStocks.filter((s) => s.pnl > 0).length;
  const inLoss = enrichedStocks.filter((s) => s.pnl < 0).length;

  return (
    <Box p={3}>
      <Typography variant="h5" align="center" sx={{ fontWeight: "bold", color: "#fff", backgroundColor: "#e53935", py: 2, borderRadius: 1 }}>
        🪙 Net Value Worth Sell
        <br />
        <Typography variant="h4" component="div" sx={{ fontWeight: "bold", mt: 1 }}>
          ₹{netStockValue.toLocaleString(undefined, { maximumFractionDigits: 2 })}
        </Typography>
        <Typography variant="body2" sx={{ mt: 1 }}>
          Total current value of all overdue positions
        </Typography>
      </Typography>

      <Grid container spacing={2} mt={2}>
        <Grid item xs={12} md={3}>
          <Card sx={{ background: "linear-gradient(90deg,#5e70ff,#6b43b3)", color: "#fff", textAlign: "center" }}>
            <CardContent>
              <Typography variant="h6" fontWeight="bold">{stocks.length}</Typography>
              <Typography variant="caption">Overdue Positions</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card sx={{ background: "linear-gradient(90deg,#5e70ff,#6b43b3)", color: "#fff", textAlign: "center" }}>
            <CardContent>
              <Typography variant="h6" fontWeight="bold">{inProfit}</Typography>
              <Typography variant="caption">In Profit</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card sx={{ background: "linear-gradient(90deg,#5e70ff,#6b43b3)", color: "#fff", textAlign: "center" }}>
            <CardContent>
              <Typography variant="h6" fontWeight="bold">{inLoss}</Typography>
              <Typography variant="caption">In Loss</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card sx={{ background: "linear-gradient(90deg,#5e70ff,#6b43b3)", color: "#fff", textAlign: "center" }}>
            <CardContent>
              <Typography variant="h6" fontWeight="bold">₹{totalPNL.toLocaleString()}</Typography>
              <Typography variant="caption">Total PnL</Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Typography variant="h6" mt={4} mb={1}>🔥 Overdue Positions Analysis</Typography>

      <TableContainer component={Paper}>
        <Table>
          <TableHead sx={{ backgroundColor: "#1976d2" }}>
            <TableRow>
              {["symbol", "quantity", "current_value", "book_value", "pnl", "overdue_by_days"].map((col) => (
                <TableCell key={col} sx={{ color: "#fff", fontWeight: "bold" }} sortDirection={orderBy === col ? order : false}>
                  <TableSortLabel
                    active={orderBy === col}
                    direction={orderBy === col ? order : "asc"}
                    onClick={() => handleSort(col)}
                  >
                    {col === "symbol" && "Stock"}
                    {col === "quantity" && "Shares"}
                    {col === "current_value" && "Current Value"}
                     {col === "book_value" && "Book Value"}
                    {col === "pnl" && "PNL"}
                    {col === "overdue_by_days" && "Overdue By (Days)"}
                    {orderBy === col ? (
                      <Box component="span" sx={visuallyHidden}>
                        {order === "desc" ? "sorted descending" : "sorted ascending"}
                      </Box>
                    ) : null}
                  </TableSortLabel>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedStocks.map((stock, idx) => (
              <TableRow key={idx} sx={{ backgroundColor: getRowColor(stock.overdue_by_days) }}>
                <TableCell>{stock.symbol}</TableCell>
                <TableCell>{stock.quantity}</TableCell>
                <TableCell>₹{stock.current_value.toLocaleString(undefined, { maximumFractionDigits: 2 })}</TableCell>
                 <TableCell>₹{stock.book_value.toLocaleString(undefined, { maximumFractionDigits: 2 })}</TableCell>
                <TableCell sx={{ color: getPNLColor(stock.pnl) }}>
                  ₹{stock.pnl.toLocaleString(undefined, { maximumFractionDigits: 2 })}
                </TableCell>
                <TableCell>{stock.overdue_by_days}</TableCell>
              </TableRow>
            ))}
            <TableRow sx={{ backgroundColor: "#0d1a4e" }}>
              <TableCell sx={{ color: "#fff", fontWeight: "bold" }}>TOTAL</TableCell>
              <TableCell sx={{ color: "#fff", fontWeight: "bold" }}>{totalQuantity}</TableCell>
              <TableCell sx={{ color: "#fff", fontWeight: "bold" }}>
                ₹{netStockValue.toLocaleString(undefined, { maximumFractionDigits: 2 })}
              </TableCell>
               <TableCell sx={{ color: "#fff", fontWeight: "bold" }}>
                ₹{netBookValue.toLocaleString(undefined, { maximumFractionDigits: 2 })}
              </TableCell>
              <TableCell sx={{ color: "#fff", fontWeight: "bold" }}>
                ₹{totalPNL.toLocaleString(undefined, { maximumFractionDigits: 2 })}
              </TableCell>
              <TableCell></TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>

      <Typography variant="h6" mt={4}>ℹ️ Information Panel</Typography>

      <Card sx={{ mt: 2, backgroundColor: "#2d4890", color: "white" }}>
        <CardContent>
          {lastCreatedStocksDue && (
            <Typography variant="body2">Last updated: {formatToIST(lastCreatedStocksDue)}</Typography>
          )}
          {overdueAlert && (
            <Alert severity="warning" sx={{ mt: 1 }}>
              {overdueAlert}
            </Alert>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default PortfolioOverdueTable;
